import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Scanner } from '@yudiel/react-qr-scanner';
import api from '../services/api';

const QrScannerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const [hasCameraPermission, setHasCameraPermission] = useState(null);
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    // Check for camera permission on mount
    checkCameraPermission();
  }, []);

  const checkCameraPermission = async () => {
    try {
      // Try to access camera to trigger permission request
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      // Stop the stream immediately after getting permission
      stream.getTracks().forEach(track => track.stop());
      setHasCameraPermission(true);
      setShowScanner(true);
    } catch (err) {
      console.log('Camera permission error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setHasCameraPermission(false);
        setError(t('qr.cameraPermissionDenied') || 'Camera permission was denied. Please allow camera access in your browser settings.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setHasCameraPermission(false);
        setError(t('qr.noCameraFound') || 'No camera found on this device.');
      } else {
        setHasCameraPermission(false);
        setError(t('qr.cameraError') || 'Could not access camera. Please use the manual URL input below.');
      }
    }
  };

  const handleScan = async (url) => {
    if (!url || isProcessing) return;

    setIsProcessing(true);
    setError('');

    try {
      const response = await api.post('/lists/import-qr', { url });
      navigate(`/lists/${response.data.id}`);
    } catch (err) {
      setError(err.message || t('qr.urlError'));
      setIsProcessing(false);
    }
  };

  const handleManualImport = () => {
    if (manualUrl.trim()) {
      handleScan(manualUrl.trim());
    }
  };

  return (
    <div>
      <button
        className="btn btn-outline"
        onClick={() => navigate('/lists')}
        style={{ marginBottom: '1rem' }}
      >
        &larr; {t('common.back')}
      </button>

      <h1 className="page-title">{t('qr.title')}</h1>

      <div className="card" style={{ marginTop: '1rem' }}>
        {error && (
          <div className="form-error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {isProcessing ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
            <p>{t('common.loading')}</p>
          </div>
        ) : (
          <>
            {/* QR Scanner */}
            {showScanner && hasCameraPermission ? (
              <div
                style={{
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  marginBottom: '1.5rem',
                  maxWidth: '400px',
                  margin: '0 auto 1.5rem',
                }}
              >
                <Scanner
                  onScan={(result) => {
                    if (result && result[0]?.rawValue) {
                      handleScan(result[0].rawValue);
                    }
                  }}
                  onError={(err) => {
                    console.error('QR Scanner error:', err);
                    setError(t('qr.scanError') || 'Error scanning QR code');
                  }}
                  constraints={{
                    facingMode: 'environment',
                  }}
                  styles={{
                    container: {
                      width: '100%',
                      aspectRatio: '1 / 1',
                    },
                    video: {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    },
                  }}
                />
                <p style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--color-text-secondary)' }}>
                  {t('qr.pointCamera')}
                </p>
              </div>
            ) : hasCameraPermission === null ? (
              <div
                style={{
                  background: 'var(--color-surface)',
                  padding: '3rem',
                  textAlign: 'center',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem',
                }}
              >
                <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
                <p style={{ color: 'var(--color-text-secondary)' }}>
                  {t('qr.requestingPermission') || 'Requesting camera permission...'}
                </p>
              </div>
            ) : (
              <div
                style={{
                  background: 'var(--color-surface)',
                  padding: '3rem',
                  textAlign: 'center',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1.5rem',
                }}
              >
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.5 }}>
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
                <p style={{ marginTop: '1rem', color: 'var(--color-text-secondary)' }}>
                  {t('qr.cameraUnavailable') || 'Camera unavailable'}
                </p>
                <button
                  className="btn btn-outline"
                  onClick={checkCameraPermission}
                  style={{ marginTop: '1rem' }}
                >
                  {t('qr.retryCamera') || 'Try again'}
                </button>
              </div>
            )}

            {/* Manual URL input as fallback */}
            <div className="form-group">
              <label className="form-label">{t('qr.manualInput') || 'Or enter URL manually:'}</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="url"
                  className="form-input"
                  value={manualUrl}
                  onChange={(e) => setManualUrl(e.target.value)}
                  placeholder="https://..."
                  style={{ flex: 1 }}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleManualImport}
                  disabled={!manualUrl.trim()}
                >
                  {t('csv.import')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QrScannerPage;
